"use client"
// Global
import { sendGTMEvent } from '@next/third-parties/google';
import { Link, LinkProps, LinkField, LinkFieldValue } from '@sitecore-content-sdk/nextjs';
import NextLink from 'next/link';
import React, { forwardRef, JSX } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { CtaProps, ctaTailwindVariant } from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useIsEditing from 'lib/hooks/useIsEditing';
import { GtmEvent } from 'lib/utils/gtm-utils';
import { useFieldWithFallbacks } from 'lib/hooks/useFieldWithFallbacks';
import {
  CtaIcons,
  CtaVariants,
  CtaIconSizes,
  CtaIconAlignments,
} from 'lib/utils/style-param-utils/modules/ctas';
import { parseLink } from 'lib/utils/link-utils';
import { RemixIcon } from 'helpers/RemixIcon';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';

const INTERNAL_LINK_REGEX = /^\/|^\#/g;

// Sitecore target options (from Content Editor dropdown)
const SITECORE_TARGET_OPTIONS = {
  ACTIVE_BROWSER: '_self', // Active Browser - same window
  CUSTOM: '|Custom', // Custom - no target attribute
  NEW_BROWSER: '_blank', // New Browser - new window/tab
} as const;

/**
 * Processes the target value from Sitecore
 * Returns undefined for custom targets (no target attribute)
 * Returns the original target for other values
 */
const processTargetValue = (target?: string): string | undefined => {
  // Handle cases where Sitecore appends "|Custom" to the actual target value
  if (target?.includes('|Custom')) {
    const actualTarget = target.replace('|Custom', '');
    // If the actual target is empty or just whitespace, return undefined (no target attribute)
    return actualTarget.trim() || undefined;
  }

  // Handle the case where target is exactly "|Custom"
  if (target === SITECORE_TARGET_OPTIONS.CUSTOM) {
    return undefined;
  }

  return target;
};

export type LinkWrapperProps = Omit<LinkProps, 'field' | 'href'> &
  CtaProps & {
    field?: LinkField | LinkFieldValue;
    fallbacks?: (LinkField | LinkFieldValue | undefined)[];
    gtmEvent?: GtmEvent;
    srOnlyText?: string;
    suppressNewTabIcon?: boolean;
    viewBox?: string;
    remixIcon?: string;
    remixIconSize?: 'sm' | 'm' | 'md' | 'lg' | 'xl';
  };

const linkWrapperTailwindVariant = tv({
  extend: ctaTailwindVariant,
  slots: {
    iconNewTab: ['align-middle', 'inline-flex', 'ml-2', '-mt-1'],
  },
  variants: {
    iconAlignment: {
      left: {
        base: ['flex-row'],
        icon: [],
        iconNewTab: ['align-middle', 'inline-flex', '-mt-1'],
      },
      right: {
        base: ['flex-row'],
        icon: [],
        iconNewTab: ['align-middle', 'inline-flex', '-mt-1'],
      },
    },
  },
});

const LinkWrapper = forwardRef<HTMLAnchorElement, LinkWrapperProps>(
  (originalProps: LinkWrapperProps, ref): JSX.Element | null => {
    const {
      onClick,
      children,
      className,
      ctaStyle,
      ctaIcon: ctaIconOverride,
      ctaIconAlignment: ctaIconAlignmentOverride,
      ctaVariant: ctaVariantOverride,
      ctaVisibility: ctaVisibilityOverride,
      ctaComponentClass,
      editable = true,
      field,
      fallbacks,
      gtmEvent,
      showLinkTextWithChildrenPresent = false,
      srOnlyText,
      suppressNewTabIcon,
      viewBox,
      ctaIconSize,
      remixIcon,
      remixIconSize = 'md',
      'aria-label': ariaLabelProp,
      ...props
    } = originalProps;

    const ctaIcon = ctaIconOverride ?? ctaStyle?.ctaIcon;
    const ctaVariant = ctaVariantOverride ?? ctaStyle?.ctaVariant ?? 'tertiary';
    const ctaIconAlignment = ctaIconAlignmentOverride ?? ctaStyle?.ctaIconAlignment ?? 'right';
    const ctaVisibility = ctaVisibilityOverride ?? ctaStyle?.ctaVisibility ?? 'visible';

    const isPagesEditing = useIsEditing();
    const isEditing = isPagesEditing && editable;

    const { renderField } = useFieldWithFallbacks(field, fallbacks);

    let effectiveCtaComponentClass = ctaComponentClass;
    if (effectiveCtaComponentClass === 'default') {
      effectiveCtaComponentClass =
        ctaVariant === 'primary'
          ? 'component-hero-button-color-1'
          : 'component-hero-button-color-2';
    }

    // If there's no field, don't render anything. This should only be the case
    // if the field itself is undefined, which should only happen if code is deployed
    // before data templates are published, so this is just a safety check
    if (!renderField) {
      return <></>;
    }

    // Clone the object so we don't modify the original.
    // This addresses some edge cases issues when the same link is rendered more than once
    // and we're modifying the link.  While it may not always be needed, it's safer to include
    const clonedField = structuredClone(renderField);

    // Standardize the field because it can either be LinkField or LinkFieldValue
    const fieldValue: LinkFieldValue = {
      ...((clonedField as LinkField)?.value ?? (clonedField as LinkFieldValue)),
    };

    const { target, title } = fieldValue;

    const {
      realText,
      shouldRender,
      isInternalAnchor,
      isCustomProtocol,
      parsedUrl: originalParsedUrl,
      isQueryString,
    } = parseLink(fieldValue, children, showLinkTextWithChildrenPresent);

    const parsedUrl = originalParsedUrl;
    // Handle the specific case for /our-people/a/somename URLs
    if (parsedUrl?.pathname) {
      const peopleRegex = /^\/our-people\/[a-zA-Z]\/(.*)/;
      const match = parsedUrl.pathname.match(peopleRegex);
      if (match && match[1]) {
        parsedUrl.pathname = `/our-people/${match[1]}`;
      }
    }

    /*
     * RENDERING
     */

    const { base } = linkWrapperTailwindVariant({
      iconAlignment: ctaIconAlignment,
      variant: ctaVariant,
      visibility: ctaVisibility,
      style: ctaVariant === 'custom' ? 'primary' : ctaVariant,
    });

    // If editing, always render
    if (isEditing) {
      return (
        <EditModeLink
          {...originalProps}
          {...{
            ctaIcon,
            ctaIconAlignment,
            ctaVariant,
            ctaVisibility,
            ctaComponentClass: effectiveCtaComponentClass ?? 'default',
          }}
          ref={ref}
          field={clonedField}
        />
      );
    }

    if (!shouldRender || !parsedUrl) {
      return <></>;
    }

    /*
     * EVENT HANDLERS
     */

    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (renderField?.value) {
        const linkName = realText || title;
        const isNavEvent = gtmEvent?.event === 'nav_click';
        const componentName = gtmEvent?.['gtm.element.dataset.gtmComponentName'];

        const gtmEventInner = {
          ...gtmEvent,
          ...(isNavEvent && typeof componentName === 'string'
            ? { 'gtm.element.dataset.gtmComponentName': componentName.toLowerCase() }
            : {}),
          'gtm.element.dataset.gtmLinkName': isNavEvent
            ? (linkName ?? '').toLowerCase()
            : linkName,
          'gtm.element.dataset.gtmLinkUrl': parsedUrl.href,
        };

        sendGTMEvent(gtmEventInner);
      }

      if (onClick) onClick(e);
    };
    const cmsAriaLabel =
      typeof title === 'string' && title.trim() !== '' ? title.trim() : undefined;

    /** The link field's own `text`, guarded without optional chaining inside `in`. */
    const fieldLinkText =
      field && typeof field.value === 'object' && field.value !== null && 'text' in field.value
        ? (field.value as { text?: string }).text
        : undefined;
        
    const accessibleText: string | undefined =
      cmsAriaLabel ||
      ariaLabelProp ||
      realText ||
      fieldLinkText ||
      (gtmEvent?.component_header ? `Read more for ${gtmEvent.component_header}` : undefined);

    /*
     * Next/Link doesn't handle named anchors. At all. In the event that the user has defined a named anchor
     * (i.e. anchor has a value, href has no value, and linktype is "internal"),
     * then use a standard anchor tag to render the link.
     */
    if (isInternalAnchor || isCustomProtocol) {

      return (
        <a
          {...props}
          className={[className, base()].join(' ')}
          data-component="helpers/sitecorewrappers/linkwrapper"
          {...editingHydrationProps(isPagesEditing)}
          href={isInternalAnchor ? parsedUrl.hash : parsedUrl.href}
          onClick={handleOnClick}
          ref={ref}
          title={title || realText || undefined}
          aria-label={accessibleText}
        >
          {ctaIconAlignment === 'left' && (
            <ScreenReaderOnlyTextAndNewTabIcon
              remixIcon={remixIcon}
              remixIconSize={remixIconSize}
              target={processTargetValue(target)}
              srOnlyText={srOnlyText}
              suppressNewTabIcon={suppressNewTabIcon}
              ctaIcon={ctaIcon}
              viewBox={viewBox}
              ctaVariant={ctaVariant}
              style={ctaVariant}
              ctaIconSize={ctaIconSize}
              ctaIconAlignment={ctaIconAlignment}
            />
          )}
          {!children ? (
            <span>
              {realText ||
                (parsedUrl.href?.startsWith('/') ? parsedUrl.href.slice(1) : parsedUrl.href)}
            </span>
          ) : (
            realText && <span>{realText}</span>
          )}
          {children}
          {ctaIconAlignment !== 'left' && (
            <ScreenReaderOnlyTextAndNewTabIcon
              remixIcon={remixIcon}
              remixIconSize={remixIconSize}
              target={processTargetValue(target)}
              srOnlyText={srOnlyText}
              suppressNewTabIcon={suppressNewTabIcon}
              ctaIcon={ctaIcon}
              viewBox={viewBox}
              ctaVariant={ctaVariant}
              style={ctaVariant}
              ctaIconSize={ctaIconSize}
              ctaIconAlignment={ctaIconAlignment}
            />
          )}
        </a>
      );
    }


    return (
      <NextLink
        {...props}
        className={[className, base()].join(' ')}
        data-component="helpers/sitecorewrappers/linkwrapper"
        {...editingHydrationProps(isPagesEditing)}
        href={isQueryString ? `${parsedUrl.href}${parsedUrl.search}${parsedUrl.hash}` : parsedUrl.href + parsedUrl.hash}
        onClick={handleOnClick}
        ref={ref}
        target={processTargetValue(target)}
        title={title || realText || undefined}
        aria-label={accessibleText}
      >
        {ctaIconAlignment === 'left' && (
          <ScreenReaderOnlyTextAndNewTabIcon
            remixIcon={remixIcon}
            remixIconSize={remixIconSize}
            target={processTargetValue(target)}
            srOnlyText={srOnlyText}
            suppressNewTabIcon={suppressNewTabIcon}
            ctaIcon={ctaIcon}
            viewBox={viewBox}
            ctaVariant={ctaVariant}
            style={ctaVariant}
            ctaIconSize={ctaIconSize}
            ctaIconAlignment={ctaIconAlignment}
          />
        )}
        {realText ? <span>{realText}</span> : null}
        {children}

        {ctaIconAlignment !== 'left' && (
          <ScreenReaderOnlyTextAndNewTabIcon
            remixIcon={remixIcon}
            remixIconSize={remixIconSize}
            target={processTargetValue(target)}
            srOnlyText={srOnlyText}
            suppressNewTabIcon={suppressNewTabIcon}
            ctaIcon={ctaIcon}
            viewBox={viewBox}
            ctaVariant={ctaVariant}
            style={ctaVariant}
            ctaIconSize={ctaIconSize}
            ctaIconAlignment={ctaIconAlignment}
          />
        )}
      </NextLink>
    );
  }
);

LinkWrapper.displayName = 'LinkWrapper';

export default LinkWrapper;

function ScreenReaderOnlyTextAndNewTabIcon({
  srOnlyText,
  suppressNewTabIcon,
  target,
  ctaIcon,
  viewBox,
  ctaVariant,
  ctaIconSize = 's',
  ctaIconAlignment = 'right',
  remixIcon,
  remixIconSize = 'md',
}: {
  srOnlyText?: string;
  suppressNewTabIcon?: boolean;
  target?: string;
  ctaIcon?: CtaIcons;
  viewBox?: string;
  ctaVariant?: CtaVariants;
  style?: CtaVariants;
  ctaIconSize?: CtaIconSizes;
  ctaIconAlignment?: CtaIconAlignments;
  remixIcon?: string;
  remixIconSize?: 'sm' | 'm' | 'md' | 'lg' | 'xl';
}) {
  // Don't show new tab icon or screen reader text for custom targets
  const shouldShowNewTabIcon = target === SITECORE_TARGET_OPTIONS.NEW_BROWSER;

  if (!shouldShowNewTabIcon && !srOnlyText && !ctaIcon && !remixIcon) {
    return <></>;
  }

  const { icon, iconNewTab } = linkWrapperTailwindVariant({
    iconAlignment: ctaIconAlignment,
    variant: ctaVariant,
    style: ctaVariant === 'custom' ? 'primary' : ctaVariant,
  });

  const Icon = remixIcon ? (
    <RemixIcon className={icon()} name={remixIcon} size={remixIconSize} viewBox={viewBox} />
  ) : ctaIcon ? (
    <SvgIcon className={icon()} icon={ctaIcon} size={ctaIconSize} viewBox={viewBox} />
  ) : !suppressNewTabIcon && shouldShowNewTabIcon ? (
    <SvgIcon className={iconNewTab()} icon="new-tab" size={ctaIconSize} viewBox={viewBox} />
  ) : null;

  return (
    <>
      {Icon}

      {srOnlyText ? (
        <span className="sr-only">
          {/* Preserve a single space character before SR Tab Text */}
          {`${srOnlyText ? srOnlyText : ''}${shouldShowNewTabIcon ? ' (Opens in a new tab)' : ''}`}
        </span>
      ) : null}
    </>
  );
}

const EditModeLink = forwardRef<HTMLAnchorElement, LinkWrapperProps>(
  (
    {
      ctaIconAlignment,
      ctaVariant,
      ctaVisibility,
      ctaIcon,
      ctaComponentClass,
      className,
      field,
      children,
      remixIcon,
      remixIconSize = 'md',
      // Even though we don't use these fields, they are here so they get removed from the props
      // Otherwise we get a warning about unknown props on the Link component
      ctaStyle: _ctaStyle,
      ctaIconSize: _ctaIconSize,
      editable: _editable,
      fallbacks: _fallbacks,
      gtmEvent: _gtmEvent,
      showLinkTextWithChildrenPresent: _showLinkTextWithChildrenPresent,
      srOnlyText: _srOnlyText,
      suppressNewTabIcon: _suppressNewTabIcon,
      viewBox: _viewBox,
      ...props
    },
    ref
  ) => {
    let effectiveCtaComponentClass = ctaComponentClass;
    if (effectiveCtaComponentClass === 'default') {
      effectiveCtaComponentClass =
        ctaVariant === 'primary'
          ? 'component-hero-button-color-1'
          : 'component-hero-button-color-2';
    }

    const { base, icon } = linkWrapperTailwindVariant({
      iconAlignment: ctaIconAlignment,
      variant: ctaVariant,
      visibility: ctaVisibility,
      style: ctaVariant === 'custom' ? 'primary' : ctaVariant,
    });
    const editableClonedField = field?.value ? (field as LinkField) : { value: { ...field } };

    // The Link component, in its editable configuration, doesn't handle internal links correctly; i.e. it won't render the link text without a value for href.
    // For internal links, we'll forcibly set the href to '/' to work around the issue in a way that doesn't otherwise affect the editing experience.
    if (
      editableClonedField?.value?.linktype === 'internal' &&
      editableClonedField.value.href === ''
    )
      editableClonedField.value.href = '/';

    return (
      // Adding the CSS classes to a wrapping div so we can include the icon
      <div className={[className, base()].join(' ')} suppressHydrationWarning>
        {ctaIconAlignment === 'left' && ctaIcon && (
          <SvgIcon className={icon()} icon={ctaIcon} size="xs" />
        )}
        {ctaIconAlignment === 'left' && remixIcon && (
          <RemixIcon className={icon()} name={remixIcon} size={remixIconSize} />
        )}
        <Link
          {...props}
          field={editableClonedField}
          // className={`${title ? 'flex items-center' : ''}`}
          internalLinkMatcher={INTERNAL_LINK_REGEX}
          ref={ref}
          suppressHydrationWarning
        >
          {children}
        </Link>
        {/* When in edit mode we cannot render anything inside the Link tag (cause duplicate link), 
          but we can rendering it outside of the link and move the styling to a parent div */}
        {ctaIconAlignment !== 'left' && ctaIcon && (
          <SvgIcon className={icon()} icon={ctaIcon} size="xs" />
        )}
        {ctaIconAlignment !== 'left' && remixIcon && (
          <RemixIcon className={icon()} name={remixIcon} size={remixIconSize} />
        )}
      </div>
    );
  }
);

EditModeLink.displayName = 'EditModeLink';
